"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import {
  Check,
  ChevronRight,
  Lock,
  Plug,
  Sparkles,
  LogIn,
} from "lucide-react";
import type { AiProfile } from "@/types/ai";
import { getPowerUpsForBuilder } from "@/lib/powerups";
import type { PowerUpDef } from "@/lib/powerups";
import { getUnlockedPowerUpListingIds } from "@/lib/powerups";
import { createBlankWorkflow } from "@/lib/agentic-workflows";
import { getUserId } from "@/lib/user/identity";
import { db } from "@/lib/db";
import { getIntegrationsForCurrentUser } from "@/lib/integrations/scope";
import type { MarketplaceListing } from "@/types/monetization";
import type { Integration } from "@/types/integration";
import ProductIcon from "@/components/ui/ProductIcon";

const POWER_UP_SEED: (Omit<MarketplaceListing, "createdAt" | "updatedAt"> & { powerUpId: string })[] = [
  { id: "powerup-advanced-browser", profileId: "platform", powerUpId: "advancedBrowser", title: "Advanced browser", description: "Browse, fill forms, extract data.", price: 499, currency: "USD", category: "power_up", tags: [], featured: true, salesCount: 0, rating: 0, reviewCount: 0 },
  { id: "powerup-voice-cloning", profileId: "platform", powerUpId: "voiceCloning", title: "Voice cloning", description: "Custom voice for your AI.", price: 999, currency: "USD", category: "power_up", tags: [], featured: true, salesCount: 0, rating: 0, reviewCount: 0 },
];

const FEATURED_INTEGRATION_TYPES = ["gmail", "google_calendar", "telegram", "discord", "slack", "whatsapp", "notion", "github"] as const;

async function ensurePowerUpListings() {
  if (typeof window === "undefined" || !db?.marketplaceListings) return;
  const count = await db.marketplaceListings.where("category").equals("power_up").count();
  if (count > 0) return;
  const now = Date.now();
  for (const row of POWER_UP_SEED) {
    await db.marketplaceListings.add({ ...row, createdAt: now, updatedAt: now });
  }
}

interface AppItem {
  id: string;
  type: "power-up" | "integration";
  name: string;
  description: string;
  icon: string | React.ComponentType<{ className?: string }>;
  enabled: boolean;
  isConnected?: boolean;
  stock?: boolean;
  listingId?: string;
}

interface DiscoverItem {
  id: string;
  type: "power-up" | "integration";
  name: string;
  description: string;
  icon: string | React.ComponentType<{ className?: string }>;
  action: "unlock" | "connect";
  price?: string;
  listingId?: string;
  integrationType?: string;
}

interface Props {
  profile?: Partial<AiProfile>;
  onUpdate?: (updated: Partial<AiProfile>) => Promise<void>;
  /** compact = builder sidebar; full = marketplace page */
  variant?: "compact" | "full";
  primaryColor?: string;
  textColor?: string;
  textSecondaryColor?: string;
}

export default function UnifiedAppsPanel({
  profile,
  onUpdate,
  variant = "compact",
  primaryColor = "#6366f1",
  textColor = "#fff",
  textSecondaryColor = "rgba(255,255,255,0.6)",
}: Props) {
  const caps = profile?.capabilities ?? {};
  const canEditPowerUps = !!profile && !!onUpdate;
  const [yourApps, setYourApps] = useState<AppItem[]>([]);
  const [discover, setDiscover] = useState<DiscoverItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);

  const load = async () => {
    await ensurePowerUpListings();
    const userId = getUserId();
    const powerUps = await getPowerUpsForBuilder(userId);
    const unlocked = await getUnlockedPowerUpListingIds(userId);
    const integrations: Integration[] = typeof db !== "undefined" && db?.integrations
      ? await getIntegrationsForCurrentUser()
      : [];
    const powerUpListings = db?.marketplaceListings
      ? await db.marketplaceListings.where("category").equals("power_up").toArray()
      : [];

    const your: AppItem[] = [];
    powerUps.forEach((p) => {
      const enabled = !!caps[p.id as keyof typeof caps]?.enabled;
      your.push({
        id: p.id,
        type: "power-up",
        name: p.label,
        description: p.description ?? "",
        icon: p.icon ?? "",
        enabled,
        stock: p.stock,
      });
    });
    integrations.forEach((i) => {
      your.push({
        id: i.type,
        type: "integration",
        name: i.name,
        description: i.description || "",
        icon: i.icon,
        enabled: i.enabled,
        isConnected: Object.keys(i.config || {}).length > 0,
      });
    });
    setYourApps(your);

    const disc: DiscoverItem[] = [];
    powerUpListings.forEach((l) => {
      if (unlocked.has(l.id)) return;
      const row = l as MarketplaceListing & { powerUpId?: string };
      if (!row.powerUpId) return;
      disc.push({
        id: row.powerUpId,
        type: "power-up",
        name: row.title,
        description: row.description,
        icon: Plug,
        action: "unlock",
        price: `$${(row.price / 100).toFixed(2)}`,
        listingId: row.id,
      });
    });
    const connectedTypes = new Set(integrations.map((i) => i.type));
    FEATURED_INTEGRATION_TYPES.forEach((type) => {
      if (connectedTypes.has(type)) return;
      const name = type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      disc.push({
        id: type,
        type: "integration",
        name,
        description: `Connect ${name}`,
        icon: type,
        action: "connect",
        integrationType: type,
      });
    });
    setDiscover(disc);
  };

  useEffect(() => {
    let cancelled = false;
    load().then(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [caps]);

  const togglePowerUp = async (id: string, enabled: boolean) => {
    if (!onUpdate) return;
    const current = caps[id as keyof typeof caps] as { enabled?: boolean; workflow?: unknown } | undefined;
    let nextCap = { ...(current || {}), enabled };
    if (id === "agenticWorkflows" && enabled && !current?.workflow && profile?.id) {
      nextCap = { ...nextCap, workflow: createBlankWorkflow(profile.id) };
    }
    const next = {
      ...profile,
      capabilities: {
        ...caps,
        [id]: nextCap,
      },
    };
    await onUpdate(next);
  };

  const toggleIntegration = async (id: string, enabled: boolean) => {
    if (!db?.integrations) return;
    const list = await getIntegrationsForCurrentUser();
    const rec = list.find((i) => i.type === id);
    if (rec) await db.integrations.update(rec.id, { enabled, updatedAt: Date.now() });
    setYourApps((prev) => prev.map((a) => (a.id === id && a.type === "integration" ? { ...a, enabled } : a)));
  };

  const handleUnlock = async (listingId: string) => {
    setPurchasing(listingId);
    try {
      await db.purchases.add({
        id: crypto.randomUUID(),
        listingId,
        buyerId: getUserId(),
        purchasedAt: Date.now(),
      });
      const powerUpListings = await db.marketplaceListings.where("category").equals("power_up").toArray();
      const listing = powerUpListings.find((l) => l.id === listingId) as (MarketplaceListing & { powerUpId?: string }) | undefined;
      if (listing?.powerUpId) {
        await load();
      }
    } finally {
      setPurchasing(null);
    }
  };

  const isCompact = variant === "compact";
  const base = "rounded-lg border border-white/10 bg-white/5";

  if (loading) {
    return (
      <div className="py-4 text-sm text-white/50">Loading apps…</div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Your apps */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">Your apps</h3>
        <div className="space-y-1">
          {yourApps.map((app) => (
            <div
              key={`${app.type}-${app.id}`}
              className={`${base} flex items-center gap-3 px-3 py-2.5`}
            >
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                {typeof app.icon === "string" ? (
                  <ProductIcon icon={app.icon} size={20} />
                ) : (
                  (() => {
                    const Icon = app.icon;
                    return <Icon className="w-5 h-5 text-white/80" />;
                  })()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-white text-sm truncate">{app.name}</div>
                {!isCompact && (
                  <div className="text-xs text-white/50 truncate">{app.description}</div>
                )}
              </div>
              {app.type === "power-up" && !canEditPowerUps ? (
                <Link
                  href="/builder"
                  className="flex-shrink-0 text-xs font-medium px-2 py-1 rounded border border-white/30 text-white/80 hover:bg-white/10 transition"
                >
                  Open in Builder
                </Link>
              ) : (
                <button
                  type="button"
                  role="switch"
                  aria-checked={app.enabled}
                  onClick={() => {
                    if (app.type === "power-up") togglePowerUp(app.id, !app.enabled);
                    else toggleIntegration(app.id, !app.enabled);
                  }}
                  className={`flex-shrink-0 w-10 h-6 rounded-full transition relative ${
                    app.enabled ? "bg-purple-500" : "bg-white/20"
                  }`}
                  style={app.enabled ? { backgroundColor: primaryColor } : {}}
                >
                  <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition left-1 ${
                      app.enabled ? "translate-x-5" : "translate-x-0"
                    }`}
                  />
                </button>
              )}
            </div>
          ))}
          {yourApps.length === 0 && (
            <p className="text-sm text-white/40 py-2">No apps yet. Add some below.</p>
          )}
        </div>
      </div>

      {/* Discover */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-white/50 mb-2">Discover</h3>
        <div className="space-y-1">
          {discover.map((item) => (
            <div
              key={`${item.type}-${item.id}`}
              className={`${base} flex items-center gap-3 px-3 py-2.5`}
            >
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                {typeof item.icon === "string" ? (
                  <ProductIcon icon={item.icon} size={20} />
                ) : (
                  (() => {
                    const Icon = item.icon;
                    return <Icon className="w-5 h-5 text-white/80" />;
                  })()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-white text-sm truncate">{item.name}</div>
                {!isCompact && (
                  <div className="text-xs text-white/50 truncate">{item.description}</div>
                )}
              </div>
              {item.action === "unlock" ? (
                <button
                  type="button"
                  disabled={purchasing === item.listingId}
                  onClick={() => item.listingId && handleUnlock(item.listingId)}
                  className="flex-shrink-0 px-2.5 py-1 rounded-md text-xs font-medium text-white bg-white/20 hover:bg-white/30 disabled:opacity-50"
                  style={{ backgroundColor: primaryColor }}
                >
                  {purchasing === item.listingId ? "…" : `${item.price} · Unlock`}
                </button>
              ) : (
                <Link
                  href={`/marketplace?connect=${item.integrationType || item.id}`}
                  className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-white hover:opacity-90"
                  style={{ backgroundColor: primaryColor }}
                >
                  <LogIn className="w-3.5 h-3.5" /> Connect
                </Link>
              )}
            </div>
          ))}
          {discover.length === 0 && (
            <p className="text-sm text-white/40 py-2">You’ve got everything. Browse more below.</p>
          )}
        </div>
        <Link
          href={variant === "full" ? "#browse" : "/marketplace"}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-white/60 hover:text-white/90"
        >
          Browse all apps <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
